"use client";

import React, { useRef } from "react";
import Image from "next/image";

import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";

import { CTAButton } from "@/shadcn/ui/button";
import { Motion } from "@/components/global/utils/Motion";
import bg from "@/public/global/bg-art.jpg";
import heroImg from "@/public/aboutUs/heroImg.png";
import { PageAboutUsSchema } from "@/sanity/pages/PageAboutUsTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";
import CryptoPillar from "@/components/global/ui/CryptoPillar";
import { useScroll, useTransform } from "framer-motion";
import { cn } from "@/lib/utils";


export default function Hero({ data }: { data: PageAboutUsSchema }) {
  
  const parallaxRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: parallaxRef,
    offset: ["start end", "end start"],
  });
  const yEthereum = useTransform(scrollYProgress, [0, 1], ["-3%", "3%"]);
  const yBitcoin = useTransform(scrollYProgress, [0, 1], ["5%", "-5%"]);

  const isBrazil = data?.settings?.slug?.current?.endsWith("/br");

  return (
    <section className="w-full pt-[2.25rem] lg:pt-[5rem] px-[2rem] lg:px-[5rem]">
      <div className="relative mx-auto w-full bg-gradient-to-b max-w-[230.25rem] pt-[10rem] lg:pt-[16rem] rounded-[2.25rem] lg:rounded-[5.25rem] overflow-clip">
        <div className="absolute left-0 top-0 w-full h-full bg-gradient-to-b from-[#F6F9F7] to-[#E6F2EB] z-0">
          <div className="absolute left-0 top-0 w-full lg:w-[65%] h-full bg-gradient-to-b lg:bg-gradient-to-r from-[#E6F2EB]/90 via-[#E6F2EB]/[35%] to-transparent z-[1]"></div>
          <Image
            className="absolute left-1/2 -translate-x-1/2 bottom-0 !w-[max(100%,90rem)] !max-w-none !h-full object-cover object-[50%_20%] mix-blend-luminosity opacity-[.36] z-[0]"
            src={bg}
            quality={100}
            alt="background art"
          />
        </div>
        
        <div className="flex flex-col lg:flex-row items-center lg:justify-between max-w-[180rem] mx-auto py-[6.91rem] lg:py-0">
          <div className={cn("relative flex flex-col mb-[12rem] z-[1] text-center lg:text-start items-center lg:items-start",
          isBrazil ? "lg:max-w-[62rem]" : "lg:max-w-[53rem]")}>
            {data.heroChip && <ChipSerif text={data.heroChip} />}
            {data?.heroTitle && (
              <PortableText
                value={data?.heroTitle ?? {}}
                components={customRichTextComponent({
                  block: {
                    h1: ({ children }) => (
                      <div className="w-full overflow-clip">
                        <Motion
                          type="h1"
                          className="font-instrument-sans text-[#103037] text-[5.75rem] lg:text-[7rem] leading-[112%] font-[600] tracking-[-0.165rem] lg:tracking-[-0.21rem]"
                          initial={{ opacity: 0, y: 30 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            duration: 0.6,
                            delay: 0.3,
                            ease: [0.25, 0.46, 0.45, 0.94],
                          }}
                        >
                          {children}
                        </Motion>
                      </div>
                    ),
                    normal: ({ children }) => (
                      <div className="max-w-[38.5rem] lg:max-w-none w-full overflow-clip">
                        <Motion
                          type="p"
                          className="text-black/70 text-[2rem] font-[400] leading-[150%] mb-0"
                          initial={{ opacity: 0, y: 30 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            duration: 0.6,
                            delay: 0.5,
                            ease: [0.25, 0.46, 0.45, 0.94],
                          }}
                        >
                          {children}
                        </Motion>
                      </div>
                    ),
                  },
                })}
              />
            )}
          </div>

          <div className="relative w-auto lg:h-[55rem]">
            <Motion
              className="relative max-w-[107.875rem] overflow-visible bottom-[-5rem] lg:bottom-0 h-full"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.7,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
            >
              <Image className="scale-150 lg:scale-100 !h-full !w-auto" src={heroImg} quality={100} alt=""></Image>
            </Motion>

            <CryptoPillar
              className="bottom-[-5.25rem] lg:bottom-[2rem] left-[calc(50%-23rem)] lg:left-[calc(50%-33rem)] w-[5.5rem] h-[22.66188rem] lg:h-[25rem]"
              type="ethereum"
              style={{ y: yEthereum, scale: typeof window !== 'undefined' && window.innerWidth >= 1024 ? 1.15 : .80 }}
            />
            <CryptoPillar
              className="bottom-[-4rem] lg:bottom-[-7rem] left-[calc(50%+18rem)] lg:left-[calc(50%+30rem)] w-[5.5rem] h-[22.66188rem] lg:h-[35rem]"
              type="bitcoin"
              style={{ y: yBitcoin, scale: typeof window !== 'undefined' && window.innerWidth >= 1024 ? 1.15 : .80 }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
